import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Calendar, FileText, Send, Award } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useConference } from '@/context/ConferenceContext';

const CallForPapers = () => {
  const { importantDetails, siteConfig, getImportantDates } = useConference();

  const dates = getImportantDates();
  const importantDates = [
    { label: 'Abstract Submission Deadline', date: dates.abstract_submission_deadline, icon: Send },
    { label: 'Registration Opens', date: dates.registration_opens, icon: Calendar },
    { label: 'Early Bird Deadline', date: dates.EarlyBird, icon: Award },
    { label: 'Mid Term Deadline', date: dates.mid_term, icon: FileText },
    { label: 'Late Registration', date: importantDetails?.Late_registration, icon: Calendar },
    { label: 'On Spot Registration', date: dates.OnSpot, icon: Calendar },
    { label: 'Conference Dates', date: importantDetails?.ConferenceDates, icon: Calendar },
  ];

  return (
    <>
      <Helmet>
        <title>Call for Papers - {importantDetails?.ConferenceTitle?.replace(/<[^>]*>/g, '') || siteConfig?.name || 'Conference'}</title>
        <meta name="description" content="Submit your research to CCAI-2026" />
      </Helmet>

      <div className="bg-white min-h-screen">
        {/* Hero */}
        <section className="hero-gradient py-16">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="text-center"
            >
              <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">Call for Papers</h1>
              <p className="text-lg text-gray-600 max-w-3xl mx-auto mb-8">
                Submit your research and contribute to the advancement of civil engineering
              </p>
              <Link to="/submit">
                <Button size="lg" data-testid="submit-paper-btn" className="bg-sky-500 hover:bg-sky-600">
                  <Send size={20} className="mr-2" />
                  Submit Your Abstract
                </Button>
              </Link>
            </motion.div>
          </div>
        </section>

        {/* Important Dates */}
        <section className="section-spacing">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-3xl font-bold text-gray-900 mb-8 text-center">Important Dates</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              {importantDates.map((item, index) => {
                const Icon = item.icon;
                return (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    transition={{ delay: index * 0.1 }}
                    viewport={{ once: true }}
                    className="card-hover p-6 bg-white rounded-2xl border-2 border-gray-100 shadow-sm text-center"
                    data-testid={`important-date-${index}`}
                  >
                    <div className="inline-flex w-14 h-14 rounded-xl bg-sky-100 items-center justify-center mb-4">
                      <Icon className="text-sky-500" size={28} />
                    </div>
                    <h3 className="text-lg font-semibold text-gray-900 mb-2">{item.label}</h3>
                    <p className="text-sky-500 font-bold text-xl">{item.date}</p>
                  </motion.div>
                );
              })}
            </div>
          </div>
        </section>

        {/* Topics */}
        <section className="section-spacing bg-gray-50">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-3xl font-bold text-gray-900 mb-8 text-center">Conference Topics</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {(siteConfig?.tracks || [
                { id: 'structural', name: 'Structural Engineering', color: '#0ea5e9' },
                { id: 'geotechnical', name: 'Geotechnical Engineering', color: '#8b5cf6' },
                { id: 'transportation', name: 'Transportation Engineering', color: '#f59e0b' },
                { id: 'water', name: 'Water Resources', color: '#06b6d4' },
                { id: 'construction', name: 'Construction Management', color: '#10b981' },
                { id: 'environmental', name: 'Environmental Engineering', color: '#84cc16' },
              ]).map((track, index) => (
                <motion.div
                  key={track.id}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.1 }}
                  viewport={{ once: true }}
                  className="bg-white p-6 rounded-2xl shadow-sm"
                  data-testid={`topic-${track.id}`}
                >
                  <div
                    className="w-12 h-12 rounded-xl flex items-center justify-center mb-4"
                    style={{ backgroundColor: `${track.color}20` }}
                  >
                    <div
                      className="w-6 h-6 rounded-full"
                      style={{ backgroundColor: track.color }}
                    />
                  </div>
                  <h3 className="text-xl font-semibold text-gray-900 mb-3">{track.name}</h3>
                  <ul className="text-gray-600 text-sm space-y-2 list-disc list-inside">
                    {track.id === 'structural' && (
                      <>
                        <li>Seismic design and analysis</li>
                        <li>Steel and concrete structures</li>
                        <li>Bridge engineering</li>
                      </>
                    )}
                    {track.id === 'geotechnical' && (
                      <>
                        <li>Foundation engineering</li>
                        <li>Soil mechanics</li>
                        <li>Ground improvement</li>
                      </>
                    )}
                    {track.id === 'transportation' && (
                      <>
                        <li>Traffic engineering</li>
                        <li>Highway design</li>
                        <li>Smart transportation</li>
                      </>
                    )}
                    {track.id === 'water' && (
                      <>
                        <li>Hydraulic engineering</li>
                        <li>Water treatment</li>
                        <li>Coastal engineering</li>
                      </>
                    )}
                    {track.id === 'construction' && (
                      <>
                        <li>Project management</li>
                        <li>BIM and digital construction</li>
                        <li>Safety management</li>
                      </>
                    )}
                    {track.id === 'environmental' && (
                      <>
                        <li>Sustainable infrastructure</li>
                        <li>Green building</li>
                        <li>Environmental impact</li>
                      </>
                    )}
                  </ul>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* Author Guidelines */}
        <section className="section-spacing">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-3xl font-bold text-gray-900 mb-8 text-center">Author Guidelines</h2>
            <div className="bg-white p-6 md:p-8 rounded-2xl shadow-sm space-y-6">
              <div>
                <h3 className="text-xl font-semibold text-gray-900 mb-3">Abstract Submission</h3>
                <ul className="text-gray-600 space-y-2 list-disc list-inside">
                  <li>Abstracts should be between 250-500 words</li>
                  <li>Include title, authors, affiliations, and keywords</li>
                  <li>Clearly state the objective, methodology, and key findings</li>
                  <li>Submit through the online submission portal</li>
                </ul>
              </div>

              <div>
                <h3 className="text-xl font-semibold text-gray-900 mb-3">Full Paper Requirements</h3>
                <ul className="text-gray-600 space-y-2 list-disc list-inside">
                  <li>Full papers should be 6-8 pages (including figures and references)</li>
                  <li>Use the provided conference template (IEEE format)</li>
                  <li>Papers must be original and not previously published</li>
                  <li>All papers will undergo peer review by at least two reviewers</li>
                </ul>
              </div>

              <div>
                <h3 className="text-xl font-semibold text-gray-900 mb-3">Format & Style</h3>
                <ul className="text-gray-600 space-y-2 list-disc list-inside">
                  <li>Paper size: A4 (210 mm × 297 mm)</li>
                  <li>Font: Times New Roman, 10pt</li>
                  <li>Margins: 25mm on all sides</li>
                  <li>Single-column format with justified text</li>
                  <li>Include line numbers for review version</li>
                </ul>
              </div>

              <div>
                <h3 className="text-xl font-semibold text-gray-900 mb-3">Templates</h3>
                <div className="flex flex-wrap gap-4">
                  <Button variant="outline" className="border-sky-500 text-sky-500 hover:bg-sky-50">
                    Download Word Template
                  </Button>
                  <Button variant="outline" className="border-sky-500 text-sky-500 hover:bg-sky-50">
                    Download LaTeX Template
                  </Button>
                </div>
              </div>

              <div>
                <h3 className="text-xl font-semibold text-gray-900 mb-3">Review Process</h3>
                <p className="text-gray-600 mb-3">
                  All submissions will be reviewed using a double-blind peer review process. Authors should ensure their manuscripts do not contain any identifying information. Reviewers will evaluate papers based on:
                </p>
                <ul className="text-gray-600 space-y-2 list-disc list-inside">
                  <li>Originality and significance of the work</li>
                  <li>Technical quality and rigor</li>
                  <li>Clarity of presentation</li>
                  <li>Relevance to the conference themes</li>
                </ul>
              </div>

              <div>
                <h3 className="text-xl font-semibold text-gray-900 mb-3">Publication</h3>
                <p className="text-gray-600">
                  Accepted papers will be published in the conference proceedings with an ISBN. Selected high-quality papers will be invited for submission to our partner journals after additional peer review and revision.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* CTA */}
        <section className="section-spacing bg-gradient-to-r from-sky-500 to-sky-600">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
              Ready to Submit Your Research?
            </h2>
            <p className="text-lg text-white/90 mb-8">
              Join researchers from around the world in sharing cutting-edge developments in civil engineering
            </p>
            <Link to="/submit">
              <Button size="lg" className="bg-white text-sky-600 hover:bg-gray-100" data-testid="cta-submit-btn">
                Submit Your Abstract Now
              </Button>
            </Link>
          </div>
        </section>
      </div>
    </>
  );
};

export default CallForPapers;
